/**
 * WarrantExposureCard — Round P140·6 (Feb 2026)
 *
 * Compact strip rendered on the Portfolio page that surfaces the
 * user's aggregate warrant exposure at a glance: total cost, total
 * market value, unrealized P/L $ / %, and how many holdings are in a
 * redemption-announced or redeemed state (i.e. imminent action needed).
 *
 * Silent (renders nothing) when the user holds zero warrants — so
 * the vast majority of DiviDrip users never see this card, and
 * warrant holders always see it above their positions table.
 *
 * Backed by GET /api/portfolio/warrants-summary.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle } from 'lucide-react';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import WarrantLeverageComparison from '@/components/WarrantLeverageComparison';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WarrantExposureCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = activePortfolioId ? { portfolio_id: activePortfolioId } : {};
    axios
      .get(`${API}/portfolio/warrants-summary`, { params, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activePortfolioId]);

  if (loading || !data?.has_warrants || !data.totals?.count) return null;

  const { totals, positions } = data;
  const pnl = totals.unrealized || 0;
  const pnlPos = pnl >= 0;
  const pnlPct = totals.unrealized_pct;
  const atRisk = totals.at_risk_count || 0;
  const fmt$ = (n) => n == null ? '—' : `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="mb-4 rounded-xl border border-amber-400/60 bg-amber-50/40 dark:bg-amber-950/25 p-3 sm:p-4"
      data-testid="warrant-exposure-card"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          <h3 className="text-sm font-bold text-foreground">Warrant Exposure</h3>
          <span className="text-[10px] font-mono text-muted-foreground">
            {totals.count} holding{totals.count === 1 ? '' : 's'}
          </span>
          {atRisk > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/25 text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/60"
              data-testid="warrant-exposure-at-risk"
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              {atRisk} redemption action required
            </span>
          )}
        </div>
        <Link to="/warrants" className="text-[11px] text-blue-600 dark:text-cyan-400 hover:underline">
          Open Warrant Screener →
        </Link>
      </div>

      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Cost</div>
          <div className="font-mono font-bold text-foreground" data-testid="warrant-exposure-cost">
            {fmt$(totals.cost)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Market Value</div>
          <div className="font-mono font-bold text-foreground" data-testid="warrant-exposure-market">
            {fmt$(totals.market_value)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Unrealized P/L</div>
          <div
            className={`font-mono font-bold ${pnlPos ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}
            data-testid="warrant-exposure-pnl"
          >
            {pnlPos ? '+' : '−'}{fmt$(pnl)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Return</div>
          <div
            className={`font-mono font-bold ${pnlPos ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}
            data-testid="warrant-exposure-pct"
          >
            {pnlPct == null ? '—' : `${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(2)}%`}
          </div>
        </div>
      </div>

      {/* Compact per-warrant table — only shows first 5 holdings; user
          clicks a ticker to open the modal for the full breakdown. */}
      <div className="mt-3 pt-2 border-t border-amber-500/25">
        <table className="w-full text-[11px]" data-testid="warrant-exposure-positions">
          <thead className="text-[9px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left py-0.5 font-semibold">Ticker</th>
              <th className="text-left py-0.5 font-semibold">Status</th>
              <th className="text-right py-0.5 font-semibold">Shares</th>
              <th className="text-right py-0.5 font-semibold">Cost</th>
              <th className="text-right py-0.5 font-semibold">Market</th>
              <th className="text-right py-0.5 font-semibold">P/L</th>
            </tr>
          </thead>
          <tbody>
            {positions.slice(0, 5).map((p) => {
              const pos = (p.unrealized_pnl || 0) >= 0;
              return (
                <tr key={p.ticker} className="border-t border-amber-500/15">
                  <td className="py-1 font-mono font-bold">
                    <Link to={`/stock/${encodeURIComponent(p.ticker)}`} className="text-blue-600 dark:text-cyan-400 hover:underline">
                      {p.ticker}
                    </Link>
                  </td>
                  <td className="py-1">
                    <span className={`text-[9px] font-bold uppercase ${
                      p.status === 'redeemed' ? 'text-rose-700 dark:text-rose-300'
                      : p.status === 'redemption_announced' ? 'text-orange-700 dark:text-orange-300'
                      : 'text-amber-700 dark:text-amber-300'
                    }`}>
                      {p.status === 'redemption_announced' ? 'REDEEM SOON' : p.status.toUpperCase()}
                    </span>
                    {p.redemption_deadline && (
                      <span className="ml-1 text-[9px] font-mono text-muted-foreground">{p.redemption_deadline}</span>
                    )}
                  </td>
                  <td className="py-1 text-right font-mono">
                    {p.shares?.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </td>
                  <td className="py-1 text-right font-mono">{fmt$(p.cost_basis)}</td>
                  <td className="py-1 text-right font-mono">{fmt$(p.market_value)}</td>
                  <td className={`py-1 text-right font-mono font-bold ${pos ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                    {p.unrealized_pnl == null ? '—' : (pos ? '+' : '−') + fmt$(p.unrealized_pnl)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {positions.length > 5 && (
          <p className="mt-1 text-[10px] text-muted-foreground italic">
            + {positions.length - 5} more warrant holding{positions.length - 5 === 1 ? '' : 's'} in your portfolio
          </p>
        )}
      </div>

      {/* Round P140·7 (Feb 2026) — 90d Leverage-vs-Underlying compare
          across every warrant the user holds. Renders inline in the
          same card so the ordering is: aggregate → per-holding →
          leverage classification. */}
      <WarrantLeverageComparison />
    </div>
  );
}
